import { AppChannel } from "@/config/channel"
import { useConfigParams } from "@/hooks/use-config-params"
import { isOk } from "@pacote/result"
import useSWR from "swr"
import z from "zod"
import { withAuthClientFetcher } from "./base-fetchers"
import { useLoginDataWithMfaConfirm } from "./hooks/use-login-data"

export type CommunityProfileStatus =
  | "HAS_COMMUNITY_PROFILE"
  | "NO_COMMUNITY_PROFILE"
  | "UNKNOWN_COMMUNITY_PROFILE_STATUS"

const getCommunityInfoUsersMeResponseSchema = z.object({
  status: z.union([z.literal("SUCCESS"), z.literal("FAILURE")]),
  requestId: z.string(),
  datetime: z.string(),
  accountCode: z.string(),
  nickname: z.string(),
  avatar: z.string(),
  updatedAt: z.string(),
  role: z.string().optional(),
})

const endpoint = "/player-ms/v1/sso/player/community-info/users/me"

async function communityFetcher(url: string) {
  return await withAuthClientFetcher({
    url,
    successSchemaValidator: getCommunityInfoUsersMeResponseSchema,
  })
}

async function communityProfileStatusFetcher(
  url: string,
): Promise<CommunityProfileStatus> {
  const communityResult = await communityFetcher(url)

  if (isOk(communityResult)) {
    return "HAS_COMMUNITY_PROFILE"
  } else if (communityResult.value.status === 404) {
    return "NO_COMMUNITY_PROFILE"
  } else {
    return "UNKNOWN_COMMUNITY_PROFILE_STATUS"
  }
}

export function useCommunityProfileStatus() {
  const { community, communityShare, channel } = useConfigParams()
  const loginData = useLoginDataWithMfaConfirm()

  const shouldFetch =
    loginData?.jwt != null &&
    (community || communityShare) &&
    channel === AppChannel.PortaleWebFullResponsiveWwwSisalIt

  return useSWR(
    () => (shouldFetch ? endpoint : null),
    communityProfileStatusFetcher,
  )
}
